import { ActivityWithAggregate, ActivityWorkflowAggregate } from '@/api/models/aggregate'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { calculateDuration } from '../slices/aggregateSlice/utils/calculateDuration'
import { deepMergeMissing } from '../slices/aggregateSlice/utils/deepMergeMissing'

const updateActivityPart = (
  activityPart: FrontActivityPart<FrontActivityWithAggregate>,
  aggregateUpdate: ActivityWithAggregate,
): FrontActivityPart<FrontActivityWithAggregate> => {
  const { activityPart: activityPartFromAggregateUpdate, ...rest } = aggregateUpdate

  return {
    ...activityPart,
    ...activityPartFromAggregateUpdate,
    contentAvailable: rest.contentAvailable,
    childActivity: {
      ...activityPart.childActivity,
      ...rest,
      children: activityPart.childActivity.children || [],
      duration: calculateDuration(activityPart?.childActivity),
    },
  }
}

const updateChildren = (
  children: FrontActivityPart<FrontActivityWithAggregate>[],
  aggregateUpdate: ActivityWithAggregate,
): FrontActivityPart<FrontActivityWithAggregate>[] => {
  return children.map((activityPart) => {
    if (activityPart?.id === aggregateUpdate?.activityPart?.id) {
      return updateActivityPart(activityPart, aggregateUpdate)
    }

    return {
      ...activityPart,
      childActivity: findAndSetAggregateToTree(activityPart.childActivity, aggregateUpdate),
      duration: calculateDuration(activityPart?.childActivity),
    }
  })
}

export const findAndSetAggregateToTree = (
  activityWithAggregate: FrontActivityWithAggregate,
  aggregateUpdate: ActivityWithAggregate,
): FrontActivityWithAggregate => {
  if (activityWithAggregate.id === aggregateUpdate.id) {
    return deepMergeMissing(
      {
        ...activityWithAggregate,
        children: activityWithAggregate.children,
        duration: calculateDuration(aggregateUpdate),
        toolUrl: aggregateUpdate.toolUrl ?? activityWithAggregate.toolUrl ?? null,
      },
      aggregateUpdate,
    )
  }

  if (activityWithAggregate.children) {
    const updatedChildren = updateChildren(activityWithAggregate.children, aggregateUpdate)

    return {
      ...activityWithAggregate,
      children: updatedChildren,
      duration: calculateDuration(activityWithAggregate),
    }
  }

  return activityWithAggregate
}

// recursively find in tree by id and update it exclude childs
export const findAndSetAggregateWorkflowToTree = (
  activityWithAggregate: FrontActivityWithAggregate,
  workflowUpdate: ActivityWorkflowAggregate,
): FrontActivityWithAggregate => {
  const activityWithAggregateUpdated = {
    ...activityWithAggregate,
  }

  if (activityWithAggregate.id === workflowUpdate.activity.id) {
    activityWithAggregateUpdated.activityWorkflowAggregate = workflowUpdate
  }

  if (activityWithAggregateUpdated.children) {
    activityWithAggregateUpdated.children = activityWithAggregateUpdated.children.map(
      (activityPart: FrontActivityPart<FrontActivityWithAggregate>) => {
        return {
          ...activityPart,
          childActivity: findAndSetAggregateWorkflowToTree(activityPart.childActivity, workflowUpdate),
        }
      },
    )
  }

  return activityWithAggregateUpdated
}
